import centroid from '@turf/centroid';
import { api, externalApi } from 'lib/request/api';
import type {
  LngLat,
  LocationSuggestion,
  SearchLocations,
} from 'components/coordinate-pins';
import type {
  ReferenceLocationResponse,
  WellsQueryResponse,
} from 'types/api-responses';
import type { TitleWindow } from 'types/window';

declare let window: TitleWindow;

/** Suggestions shown at most, every kind together. */
const LIMIT = 4;
/** Wells match a UWI or name prefix from two characters; places need a little more. */
const MIN_PLACE_TEXT = 3;

/**
 * Wells whose name or UWI matches, through the query behind Map Query's search. Only
 * wells with a surface location are kept, since a pin needs one.
 *
 * @param {string} text - What was typed.
 * @param {AbortSignal} signal - Cancels the request when the text changes.
 * @return {Promise<LocationSuggestion[]>} Up to four wells.
 */
export const searchWells = async (
  text: string,
  signal: AbortSignal
): Promise<LocationSuggestion[]> => {
  const { results } = await api
    .post('queries/wells', {
      json: { query: { text, page: 1, pageSize: LIMIT } },
      signal,
    })
    .json<{ results: WellsQueryResponse[] }>();
  return results.flatMap((well) => {
    const point = well.surfaceHole?.coordinates;
    if (!point) return [];
    return [
      {
        id: `well-${well.uwi}`,
        kind: 'well' as const,
        title: well.name || well.uwi,
        detail: [well.uwi, well.currentOperator].filter(Boolean).join(' · '),
        lngLat: [point[0], point[1]] satisfies LngLat,
      },
    ];
  });
};

interface GeocodeFeature {
  id: string;
  geometry: { coordinates: LngLat };
  properties: {
    name?: string;
    place_formatted?: string;
    full_address?: string;
  };
}

/**
 * Places from Mapbox's geocoder, US only and nearest the map first. Nothing without a
 * Mapbox token, as in tests and test mode.
 *
 * @param {string} text - What was typed.
 * @param {LngLat | undefined} near - The map's centre, to rank nearby places first.
 * @param {AbortSignal} signal - Cancels the request when the text changes.
 * @return {Promise<LocationSuggestion[]>} Up to four places.
 */
export const searchPlaces = async (
  text: string,
  near: LngLat | undefined,
  signal: AbortSignal
): Promise<LocationSuggestion[]> => {
  const token = window.CONFIG?.mapbox.token;
  if (!token || text.length < MIN_PLACE_TEXT) return [];
  const searchParams = new URLSearchParams({
    q: text,
    access_token: token,
    country: 'us',
    language: 'en',
    limit: String(LIMIT),
    autocomplete: 'true',
  });
  if (near) searchParams.set('proximity', `${near[0]},${near[1]}`);
  const { features } = await externalApi
    .get('https://api.mapbox.com/search/geocode/v6/forward', {
      searchParams,
      signal,
    })
    .json<{ features: GeocodeFeature[] }>();
  return features.map((feature) => ({
    id: `place-${feature.id}`,
    kind: 'place' as const,
    title: feature.properties.name ?? feature.properties.full_address ?? text,
    detail: feature.properties.place_formatted ?? '',
    lngLat: feature.geometry.coordinates,
  }));
};

/**
 * The middle of a survey area, read from its shape; null when it has none.
 *
 * @param {number} id - The survey area.
 * @return {Promise<LngLat | null>} Where to pin it.
 */
const locateSurveyArea = async (id: number): Promise<LngLat | null> => {
  const { geometry } = await api
    .get(`reference_locations/${id}`)
    .json<ReferenceLocationResponse>();
  if (!geometry) return null;
  const [lng, lat] = centroid(geometry).geometry.coordinates;
  return [lng, lat];
};

/**
 * Survey areas whose name matches, through the search behind the Survey Areas map.
 * The search carries no shapes, so each finds its middle only when picked.
 *
 * @param {string} text - What was typed.
 * @param {AbortSignal} signal - Cancels the request when the text changes.
 * @return {Promise<LocationSuggestion[]>} Up to four survey areas.
 */
export const searchSurveyAreas = async (
  text: string,
  signal: AbortSignal
): Promise<LocationSuggestion[]> => {
  const { results } = await api
    .post('queries/reference_locations', {
      json: { query: { text, pageSize: LIMIT } },
      signal,
    })
    .json<{ results: ReferenceLocationResponse[] }>();
  return results.map((area) => ({
    id: `survey-area-${area.id}`,
    kind: 'surveyArea' as const,
    title: area.name,
    detail: 'Survey area',
    locate: () => locateSurveyArea(area.id),
  }));
};

/**
 * Shares the slots out a turn at a time, so no kind with matches is crowded out, then
 * lists each kind's share in the order given.
 *
 * @param {LocationSuggestion[][]} kinds - Each kind's matches, best first.
 * @return {LocationSuggestion[]} Up to four, grouped by kind.
 */
const shareSlots = (kinds: LocationSuggestion[][]): LocationSuggestion[] => {
  const counts = kinds.map(() => 0);
  let left = Math.min(
    LIMIT,
    kinds.reduce((sum, list) => sum + list.length, 0)
  );
  while (left > 0) {
    kinds.forEach((list, index) => {
      if (left > 0 && counts[index] < list.length) {
        counts[index] += 1;
        left -= 1;
      }
    });
  }
  return kinds.flatMap((list, index) => list.slice(0, counts[index]));
};

/**
 * The top four for the Coordinates card's Location field: wells, then survey areas,
 * then places, each kind with matches getting a share so none is crowded out. A
 * search failing leaves the others' results; with none, it rejects, since "no matches"
 * would be untrue.
 *
 * @param {string} text - What was typed.
 * @param {LngLat | undefined} near - The map's centre.
 * @param {AbortSignal} signal - Cancels every request.
 * @return {Promise<LocationSuggestion[]>} Up to four matches.
 */
export const searchLocations: SearchLocations = async (text, near, signal) => {
  const settled = await Promise.allSettled([
    searchWells(text, signal),
    searchSurveyAreas(text, signal),
    searchPlaces(text, near, signal),
  ]);
  const kinds = settled.map((kind) =>
    kind.status === 'fulfilled' ? kind.value : []
  );
  const suggestions = shareSlots(kinds);
  const failure = settled.find((kind) => kind.status === 'rejected');
  if (suggestions.length === 0 && failure) throw failure.reason;
  return suggestions;
};
